import { useEffect, useState } from "react";
import { Bell, BellOff, Smartphone, Share, PlusSquare, MoreVertical, MoreHorizontal, ListPlus, CheckCircle2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, formatErr } from "@/lib/api";
import { enablePush, disablePush, getSubscription, isIOS, isAndroid, isStandalone, pushSupported } from "@/lib/push";
import { PageExplain } from "@/components/PageExplain";

export const Step = ({ n, icon: Icon, title, text }) => (
  <li className="flex gap-4 p-4 rounded-2xl bg-white border border-pink-100">
    <span className="shrink-0 w-9 h-9 rounded-full pink-gradient text-white font-bold flex items-center justify-center">{n}</span>
    <div className="min-w-0">
      <p className="font-bold flex items-center gap-2">{title} {Icon && <Icon className="w-4 h-4 text-rose-500" />}</p>
      <p className="text-sm text-slate-500 mt-0.5 leading-relaxed">{text}</p>
    </div>
  </li>
);

export const IOS_STEPS = [
  { icon: Smartphone, title: "In Safari öffnen", text: "Öffne diese Seite in Safari. Wichtig: Nicht im Browser von Instagram, TikTok oder WhatsApp – dort fehlt die Option. Tippe dort auf „In Safari öffnen“." },
  { icon: MoreHorizontal, title: "Drei Punkte (•••) tippen", text: "Ab iOS 26 (z.B. iPhone 17): Tippe neben der Adressleiste auf die drei Punkte (•••) und dann auf „Teilen“. Bei älteren iPhones tippst du direkt auf das Teilen-Symbol (Quadrat mit Pfeil nach oben)." },
  { icon: PlusSquare, title: "„Zum Home-Bildschirm“", text: "Scrolle im Teilen-Menü nach unten und tippe auf „Zum Home-Bildschirm“. Lass „Als Web-App öffnen“ eingeschaltet und tippe auf „Hinzufügen“." },
  { icon: ListPlus, title: "Option fehlt?", text: "Scrolle im Teilen-Menü ganz nach unten → „Aktionen bearbeiten“ → bei „Zum Home-Bildschirm“ auf das grüne Plus tippen → „Fertig“. Danach erscheint die Option." },
  { icon: Bell, title: "App öffnen & aktivieren", text: "Öffne itzPlay über das neue Icon auf deinem Home-Bildschirm, melde dich an, gehe auf „Push“ und tippe auf „Benachrichtigungen aktivieren“ → „Erlauben“." },
];
export const ANDROID_STEPS = [
  { icon: Smartphone, title: "In Chrome öffnen", text: "Öffne diese Seite in Google Chrome." },
  { icon: MoreVertical, title: "Menü öffnen", text: "Tippe oben rechts auf die drei Punkte ⋮." },
  { icon: PlusSquare, title: "„App installieren“", text: "Wähle „App installieren“ bzw. „Zum Startbildschirm hinzufügen“ (optional, aber empfohlen)." },
  { icon: Bell, title: "Aktivieren & erlauben", text: "Tippe hier auf „Benachrichtigungen aktivieren“ und bestätige mit „Zulassen“." },
];
const DESKTOP_STEPS = [
  { icon: Bell, title: "Aktivieren klicken", text: "Klicke auf „Benachrichtigungen aktivieren“." },
  { icon: CheckCircle2, title: "Erlauben", text: "Bestätige die Browser-Abfrage mit „Zulassen“. Fertig!" },
];

const PUSH_STEPS = [
  "Mit Push verpasst du nichts mehr in der Community, bekommst abends um 19 Uhr eine Erinnerung, falls dein Login-Bonus noch fehlt, und eine Glückwunsch-Nachricht, wenn du Monats-Gewinner wirst.",
  "Wähle unten dein Gerät (iPhone, Android oder Computer) und folge den Schritten.",
  "iPhone: Die App muss zuerst zum Home-Bildschirm hinzugefügt werden – sonst erlaubt Apple keine Push-Nachrichten.",
  "Tippe auf „Benachrichtigungen aktivieren“ und erlaube sie. Mit „Test senden“ prüfst du, ob alles klappt.",
];
const DEVICE_TABS = [["ios", "iPhone", IOS_STEPS], ["android", "Android", ANDROID_STEPS], ["desktop", "Computer", DESKTOP_STEPS]];
const TAB_CLS = "rounded-full px-4 py-2 data-[state=active]:bg-rose-500 data-[state=active]:text-white";

function usePushSubscription() {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { getSubscription().then((s) => setEnabled(!!s)).catch(() => {}); }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      if (enabled) await disablePush(); else await enablePush();
      toast.success(enabled ? "Benachrichtigungen deaktiviert" : "Benachrichtigungen aktiviert! 🔔");
      setEnabled(!enabled);
    } catch (e) { toast.error(e.response ? formatErr(e) : e.message); } finally { setBusy(false); }
  };
  const test = () => api.post("/push/test").then(() => toast.success("Test-Benachrichtigung gesendet!")).catch((e) => toast.error(formatErr(e)));
  return { enabled, busy, toggle, test };
}

const statusHint = (iosNeedsInstall) => {
  if (iosNeedsInstall) return "Bitte zuerst zum Home-Bildschirm hinzufügen (siehe unten).";
  if (!pushSupported()) return "Dein Browser unterstützt leider keine Push-Nachrichten.";
  return "Erhalte eine Nachricht, sobald es Neues in der Community gibt.";
};

function PushStatusCard({ enabled, busy, toggle, test, iosNeedsInstall }) {
  return (
    <div className={`rounded-3xl p-6 border flex flex-col sm:flex-row sm:items-center gap-4 justify-between ${enabled ? "bg-emerald-50 border-emerald-200" : "bg-white border-pink-100"}`} data-testid="push-status-card">
      <div className="flex items-center gap-4">
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${enabled ? "bg-emerald-500 text-white" : "bg-rose-50 text-rose-500"}`}>{enabled ? <Bell className="w-7 h-7" /> : <BellOff className="w-7 h-7" />}</div>
        <div>
          <p className="font-bold text-lg" data-testid="push-status-text">{enabled ? "Aktiv auf diesem Gerät" : "Noch nicht aktiviert"}</p>
          <p className="text-sm text-slate-500">{statusHint(iosNeedsInstall)}</p>
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={toggle} disabled={busy || iosNeedsInstall} data-testid="push-toggle-btn"
          className={`inline-flex items-center gap-2 px-5 h-12 rounded-full font-bold text-sm transition-colors disabled:opacity-50 ${enabled ? "bg-white border border-slate-200 text-slate-700" : "pink-gradient text-white pink-glow"}`}>
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} {enabled ? "Deaktivieren" : "Benachrichtigungen aktivieren"}
        </button>
        {enabled && <button onClick={test} data-testid="push-test-btn" className="inline-flex items-center gap-2 px-5 h-12 rounded-full bg-slate-900 text-white font-bold text-sm"><Send className="w-4 h-4" /> Test senden</button>}
      </div>
    </div>
  );
}

export const openShareSheet = () => {
  if (!navigator.share) { toast.error("Bitte öffne die Seite in Safari und nutze die drei Punkte (•••) → „Teilen“."); return; }
  navigator.share({ title: "itzPlay", url: `${window.location.origin}/dashboard` }).catch(() => {});
};

const IosShareHelper = () => (
  <div className="rounded-3xl p-5 border border-rose-200 bg-rose-50 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid="ios-share-helper">
    <div>
      <p className="font-bold">Abkürzung fürs iPhone</p>
      <p className="text-sm text-slate-600">Tippe auf den Button – es öffnet sich direkt das Teilen-Menü. Scrolle darin nach unten zu „Zum Home-Bildschirm“.</p>
    </div>
    <button onClick={openShareSheet} data-testid="ios-share-btn" className="shrink-0 inline-flex items-center justify-center gap-2 px-5 h-12 rounded-full pink-gradient text-white font-bold text-sm pink-glow">
      <Share className="w-4 h-4" /> Teilen-Menü öffnen
    </button>
  </div>
);

const defaultDevice = () => {
  if (isIOS()) return "ios";
  if (isAndroid()) return "android";
  return "desktop";
};

const DeviceGuide = () => (
  <Tabs defaultValue={defaultDevice()}>
    <TabsList className="rounded-full bg-white border border-pink-100 p-1 h-auto">
      {DEVICE_TABS.map(([k, label]) => <TabsTrigger key={k} value={k} data-testid={`push-tab-${k}`} className={TAB_CLS}>{label}</TabsTrigger>)}
    </TabsList>
    {DEVICE_TABS.map(([k, , steps]) => (
      <TabsContent key={k} value={k} className="mt-4">
        <ol className="space-y-3" data-testid={`push-steps-${k}`}>{steps.map((s, i) => <Step key={s.title} n={i + 1} {...s} />)}</ol>
      </TabsContent>
    ))}
  </Tabs>
);

export default function PushGuide() {
  const push = usePushSubscription();
  const iosNeedsInstall = isIOS() && !isStandalone();
  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs tracking-wider uppercase font-semibold text-rose-600">Anleitung</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Push-Benachrichtigungen aufs Handy</h1>
      </div>
      <PageExplain testId="push-explain" steps={PUSH_STEPS} />
      <PushStatusCard {...push} iosNeedsInstall={iosNeedsInstall} />
      {iosNeedsInstall && <IosShareHelper />}
      <DeviceGuide />
      <p className="text-xs text-slate-400">Tipp: Wenn keine Nachrichten ankommen, prüfe in den Handy-Einstellungen unter „Mitteilungen“, ob itzPlay bzw. dein Browser Benachrichtigungen senden darf.</p>
    </div>
  );
}
